// Health (Console D, views/health.js `#/health`; APS-324, APS-417): is the app healthy for
// everyone who uses it? One app's active devices per day with the latest release's shipping
// marked, the releases' adoption, stability by release and by kind of ending (crash-free by
// crash, out of memory, watchdog and ANR, D113), and app exits by reason per 1,000 sessions
// against the release before. Weekly and monthly active devices, app opens, logs by level and
// where force quits happen need data the API does not have yet and are not shown.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  appsQuery,
  dailyHealthQuery,
  exitsQuery,
  issuesQuery,
  lastDays,
  releasesQuery,
} from '../api/queries'
import type { ExitReason, ReleaseHealth } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import {
  btnClass,
  EmptyNote,
  Hbar,
  HovRow,
  PageHead,
  Section,
  Strip,
  type StripItem,
  Table,
} from '../components/d'
import { LineChart } from '../components/LineChart'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { allExits, exitLabel, exitRows, exitTone, per1kOf } from '../lib/exits'
import { compact, percentSpaced, shortId } from '../lib/format'
import { issueTitle } from '../lib/issues'
import { adoption, busiestApp, previousOf, releasesNewestFirst } from '../lib/releases'
import { useProjectParams } from './params'
import type { ReleasesSearch } from './search'

/** Crash-free and stability over the last week, as the mock reads them. */
export const WINDOW_DAYS = 7

/**
 * A change per 1k, signed, always one decimal with a real minus (−43.0, +10.1, 0.0): red or
 * amber when it rose, green when it fell.
 */
export function Change({ value, worse = 'c-warn' }: { value: number; worse?: string }) {
  const { number } = useI18n()
  const v = Math.round(value * 10) / 10
  const abs = number(Math.abs(v), { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return (
    <span className={v === 0 ? 'faint' : v > 0 ? worse : 'c-ok'}>
      {v > 0 ? `+${abs}` : v < 0 ? `−${abs}` : abs}
    </span>
  )
}

/**
 * Health's scope, shared with App exits: the app (the one chosen, or the one with the most
 * sessions in the week), its releases newest first, the latest and the one before it.
 */
export function useHealthScope() {
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ReleasesSearch
  // The window is fixed per visit (to the minute) so the query keys stay stable.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  // Which builds (APS-508): release ones unless the bar says debug or all; every read below takes it.
  const build = search.build ? { build: search.build } : {}
  const week = useQuery(
    releasesQuery(projectId, { ...lastDays(WINDOW_DAYS, now), ...build }, [
      'health',
      WINDOW_DAYS,
      now,
      search.build ?? 'release',
    ]),
  )
  const apps = useQuery(appsQuery(orgId, projectId))
  const appId = search.app ?? (week.data ? busiestApp(week.data) : undefined)
  const app = apps.data?.find((a) => a.id === appId)
  const list = week.data ? releasesNewestFirst(week.data, appId) : []
  const latest = list[0]
  const previous = latest ? previousOf(list, latest.release) : undefined
  const exits = useQuery({
    ...exitsQuery(
      projectId,
      appId ?? '',
      lastDays(WINDOW_DAYS, now),
      ['health', WINDOW_DAYS, now],
      search.build,
    ),
    enabled: !!appId,
  })
  // A release's exits, and whether the app sends exit reasons at all (older SDKs do not).
  const exitsOf = (release: string | undefined) =>
    exits.data?.releases.find((r) => r.release === release)
  const hasExits = (exits.data?.reasons.length ?? 0) > 0
  return {
    orgId,
    projectId,
    now,
    build,
    week,
    appId,
    app,
    android: app?.platform === 'android',
    list,
    latest,
    previous,
    exits,
    exitsOf,
    hasExits,
  }
}

export function HealthPage() {
  const i18n = useI18n()
  const { t, number, date, rich } = i18n
  // Shares as the mock writes them, with the space: 66.67 %, 0.00 %, 100 %.
  const pct = (share: number, digits = 2) => percentSpaced(i18n, share, digits)
  const navigate = useNavigate()
  const {
    orgId,
    projectId,
    now,
    build,
    week,
    appId,
    app,
    android,
    list,
    latest,
    previous,
    exitsOf,
    hasExits,
  } = useHealthScope()
  const daily = useQuery({
    ...dailyHealthQuery(projectId, appId, new Date(now).toISOString(), build),
    enabled: !!appId,
  })
  const today = useQuery({
    ...releasesQuery(projectId, { ...lastDays(1, now), app_id: appId, ...build }, [
      'health-day',
      appId,
      now,
      build.build ?? 'release',
    ]),
    enabled: !!appId,
  })
  // The top open issue of each kind (most users first), as the mock lists them under the table.
  const top = (kind: 'crash' | 'hang' | 'anr') =>
    issuesQuery(projectId, {
      app_id: appId,
      kind,
      days: WINDOW_DAYS,
      sort: 'users',
      limit: 1,
      ...build,
    })
  const topCrash = useQuery({ ...top('crash'), enabled: !!appId })
  const topHang = useQuery({ ...top(android ? 'anr' : 'hang'), enabled: !!appId })

  if (week.isPending)
    return (
      <main id="main" className="pg health">
        <Loading />
      </main>
    )
  if (week.isError)
    return (
      <main id="main" className="pg health">
        <ErrorState error={week.error} retry={() => week.refetch()} />
      </main>
    )

  const days = daily.data ?? []
  const dad = days.at(-1)?.users ?? null
  const weekAgo = days.at(-8)?.users ?? null
  const shipped = latest
    ? days.findIndex((d) => d.day.slice(0, 10) === latest.released_at.slice(0, 10))
    : -1
  const pts = (a: number | null | undefined, b: number | null | undefined) =>
    a == null || b == null ? null : (a - b) * 100
  const delta = (d: number | null, worseUp = false) => {
    if (d === null) return null
    const bad = worseUp ? d > 0 : d < 0
    const text = t('health.pts', {
      value: number(d, { signDisplay: 'exceptZero', maximumFractionDigits: 2 }),
    })
    return <span className={d === 0 ? 'faint' : bad ? 'c-crash' : 'c-ok'}>{text}</span>
  }
  const vsPrevious = previous
    ? (node: React.ReactNode) => (
        <>
          {node} {t('health.vs', { release: previous.release })}
        </>
      )
    : undefined
  const latestExits = exitsOf(latest?.release)
  const previousExits = previous ? exitsOf(previous.release) : undefined
  const quits = per1kOf(latestExits, 'user_quit')
  const quitsBefore = previousExits ? per1kOf(previousExits, 'user_quit') : null
  const exitsLink = (props: { className: string; children: React.ReactNode }) => (
    <Link
      to="/o/$orgId/p/$projectId/health/exits"
      params={{ orgId, projectId }}
      search={appId ? { app: appId } : {}}
      className={props.className}
    >
      {props.children}
    </Link>
  )
  const releaseLink =
    (release: string) => (props: { className: string; children: React.ReactNode }) => (
      <Link
        to="/o/$orgId/p/$projectId/releases"
        params={{ orgId, projectId }}
        search={{ ...(appId ? { app: appId } : {}), release }}
        className={props.className}
      >
        {props.children}
      </Link>
    )
  const strip: StripItem[] = [
    {
      dt: t('health.strip.devices'),
      dd: dad === null ? '—' : compact(i18n, dad),
      small: t('health.strip.dad'),
      d:
        dad !== null && weekAgo
          ? t('health.strip.devicesVs', {
              change: (() => {
                const c = (dad - weekAgo) / weekAgo
                return `${c > 0 ? '+' : c < 0 ? '−' : ''}${pct(Math.abs(c), 1)}`
              })(),
            })
          : undefined,
    },
    ...(latest
      ? [
          {
            dt: t('health.strip.sessions'),
            dd: number(latest.crash_free_sessions_rate * 100, { maximumFractionDigits: 2 }),
            small: '%',
            d: vsPrevious?.(
              delta(pts(latest.crash_free_sessions_rate, previous?.crash_free_sessions_rate)),
            ),
            link: releaseLink(latest.release),
          },
          ...(latest.crash_free_users_rate != null
            ? [
                {
                  dt: t('health.strip.devicesCrashFree'),
                  dd: number(latest.crash_free_users_rate * 100, { maximumFractionDigits: 2 }),
                  small: '%',
                  d: vsPrevious?.(
                    delta(pts(latest.crash_free_users_rate, previous?.crash_free_users_rate)),
                  ),
                  link: releaseLink(latest.release),
                },
              ]
            : []),
          ...(hasExits && latestExits
            ? [
                {
                  dt: t('health.strip.quits'),
                  dd: number(quits, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
                  small: t('health.per1k'),
                  d:
                    previous && quitsBefore !== null
                      ? rich('exits.vsBefore', {
                          change: <Change value={quits - quitsBefore} />,
                          release: previous.release,
                          before: number(quitsBefore, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }),
                        })
                      : undefined,
                  link: exitsLink,
                },
              ]
            : []),
        ]
      : []),
  ]
  // Newest release first, as the mock lists them (adoption() orders by share).
  const shares = today.data
    ? (() => {
        const rows = releasesNewestFirst(today.data, appId)
        const by = new Map(adoption(rows).map((a) => [a.release, a.share]))
        return rows.map((r) => ({ release: r.release, share: by.get(r.release) ?? 0 }))
      })()
    : []
  const toRelease = (release: string) => (
    <Link
      className="ul mono"
      to="/o/$orgId/p/$projectId/releases"
      params={{ orgId, projectId }}
      search={{ ...(appId ? { app: appId } : {}), release }}
      translate="no"
    >
      {release}
    </Link>
  )
  const totalSessions = list.reduce((n, r) => n + r.sessions, 0)
  // Crash-free by kind of ending (D113): the share of sessions that did not end that way. An iOS
  // hang that ends the app is the watchdog's, so ANRs are Android's only.
  const byKind = latest?.crash_free_by_kind
  const byKindBefore = previous?.crash_free_by_kind
  const kinds = byKind
    ? (['crash', 'memory', 'watchdog', ...(android ? (['anr'] as const) : [])] as const).map(
        (k) => ({
          k,
          label: t(`health.kind.${k}`),
          rate: 1 - byKind[k],
          before: byKindBefore ? 1 - byKindBefore[k] : null,
        }),
      )
    : []
  const exitList = exitRows(latestExits, previousExits)
  const toneOf = (reason: ExitReason) => {
    const tone = exitTone(reason)
    return tone ? { tone } : {}
  }
  const exitMax = Math.max(0, ...exitList.map((x) => x.v))
  const scope = app ? appLabel(app) : undefined
  const one = (v: number) => number(v, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

  return (
    <main id="main" className="pg health">
      <PageHead
        title={t('health.title')}
        meta={[
          scope,
          latest ? t('health.meta', { release: latest.release, days: WINDOW_DAYS }) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          latest ? (
            <>
              <Link
                className={btnClass()}
                to="/o/$orgId/p/$projectId/health/exits"
                params={{ orgId, projectId }}
                search={appId ? { app: appId } : {}}
              >
                {t('exits.title')}
              </Link>
              {previous ? (
                <Link
                  className={btnClass()}
                  to="/o/$orgId/p/$projectId/releases"
                  params={{ orgId, projectId }}
                  search={{
                    ...(appId ? { app: appId } : {}),
                    cand: latest.release,
                    base: previous.release,
                  }}
                >
                  {t('health.compare', { a: latest.release, b: previous.release })}
                </Link>
              ) : null}
            </>
          ) : undefined
        }
      />
      {!latest ? (
        <EmptyNote title={t('health.none')}>
          {t('health.noneBody')}
          <DebugOnlyLine projectId={projectId} scope={{ app: appId, days: WINDOW_DAYS }} empty />
        </EmptyNote>
      ) : (
        <>
          <Strip items={strip} label={t('health.title')} translate="no" />
          <div className="grid2">
            <Section
              title={t('health.devices')}
              meta={t('health.devicesMeta', { days: days.length })}
            >
              {daily.isPending ? (
                <Loading />
              ) : days.every((d) => d.users == null) ? (
                <EmptyNote title={t('health.noDevices')}>{t('health.noDevicesBody')}</EmptyNote>
              ) : (
                <>
                  <LineChart
                    points={days.map((d) => ({
                      label: date(`${d.day.slice(0, 10)}T00:00:00Z`, {
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                      }),
                      value: d.users,
                    }))}
                    format={(v) => compact(i18n, v)}
                    integer
                    label={t('health.devicesLabel', { days: days.length })}
                    marker={shipped >= 0 ? { index: shipped, label: latest.release } : undefined}
                    area
                    timeAxis
                    xName={t('chart.day')}
                  />
                  <p className="legend">
                    <span>{t('health.dadName')}</span>
                    {shipped >= 0 ? (
                      <span>{t('health.shipped', { release: latest.release })}</span>
                    ) : null}
                  </p>
                </>
              )}
            </Section>
            <Section
              title={t('health.adoption')}
              meta={t('health.adoptionMeta')}
              right={
                <Link
                  className={btnClass('ghost', 'sm')}
                  to="/o/$orgId/p/$projectId/releases"
                  params={{ orgId, projectId }}
                  search={appId ? { app: appId } : {}}
                >
                  {t('nav.releases')}
                </Link>
              }
            >
              {shares.length === 0 ? (
                <EmptyNote title={t('health.noAdoption')} />
              ) : (
                <ul className="hl-adopt" aria-label={t('health.adoption')}>
                  {shares.map((s) => (
                    <li key={s.release} className="hl-share">
                      {toRelease(s.release)}
                      <span className="b" aria-hidden="true">
                        <i style={{ width: `${Math.round(s.share * 100)}%` }} />
                      </span>
                      <span>{pct(s.share, 0)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
          <div className="grid2">
            <Section
              title={t('health.byRelease')}
              meta={t('health.lastDays', { days: WINDOW_DAYS })}
            >
              <Table label={t('health.byReleaseLabel')}>
                <thead>
                  <tr className="hl-grp">
                    <th className="f" />
                    <th />
                    <th className="num" colSpan={2}>
                      {t('health.col.crashFree')}
                    </th>
                    <th className="num last" colSpan={2}>
                      {t('health.col.counted')}
                    </th>
                  </tr>
                  <tr>
                    <th className="f">{t('health.col.release')}</th>
                    <th className="num">{t('health.col.share')}</th>
                    <th className="num">{t('health.col.sessions')}</th>
                    <th className="num">{t('health.col.devices')}</th>
                    <th className="num">{t('health.col.sessions')}</th>
                    <th className="num last">{t('health.col.devices')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r: ReleaseHealth) => (
                    <tr
                      key={r.release}
                      className={r.release === latest.release ? 'hov cur' : 'hov'}
                      tabIndex={0}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a')) return
                        navigate({
                          to: '/o/$orgId/p/$projectId/releases',
                          params: { orgId, projectId },
                          search: { ...(appId ? { app: appId } : {}), release: r.release },
                        })
                      }}
                    >
                      <td className="f">{toRelease(r.release)}</td>
                      <td className="num">
                        {pct(totalSessions ? r.sessions / totalSessions : 0, 0)}
                      </td>
                      <td className="num">{pct(r.crash_free_sessions_rate)}</td>
                      <td className="num">
                        {r.crash_free_users_rate == null ? '—' : pct(r.crash_free_users_rate)}
                      </td>
                      <td className="num">{compact(i18n, r.sessions)}</td>
                      <td className="num last">{r.users == null ? '—' : compact(i18n, r.users)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Section>
            <Section
              title={t('health.byKind')}
              meta={t('health.byKindMeta', { release: latest.release })}
            >
              <Table label={t('health.byKindLabel')}>
                <thead>
                  <tr>
                    <th className="f">{t('health.col.kind')}</th>
                    <th className="num">{t('health.col.free')}</th>
                    <th className="num">{t('health.per1k')}</th>
                    <th className="num last">
                      {previous ? t('health.col.vs', { release: previous.release }) : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kinds.map((k) => {
                    const v = k.rate * 1000
                    const before = k.before == null ? null : k.before * 1000
                    const change = before == null ? null : v - before
                    return (
                      <tr key={k.k}>
                        <td className="f">
                          <span className={`kd ${k.k}`}>{k.label}</span>
                        </td>
                        <td className="num">{pct(1 - k.rate)}</td>
                        <td className="num">
                          {number(v, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </td>
                        <td className="num last">
                          {change === null ? null : (
                            <Change value={change} worse={k.k === 'crash' ? 'c-crash' : 'c-warn'} />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              {/* The top open issue of each kind; a kind without one lists nothing. */}
              <ul className="rl hl-rl">
                {(
                  [
                    ['crash', topCrash.data?.issues[0]],
                    [android ? 'anr' : 'hang', topHang.data?.issues[0]],
                  ] as const
                ).map(([kind, issue]) =>
                  issue ? (
                    <li key={kind}>
                      <span>
                        <Link
                          className="lk"
                          to="/o/$orgId/p/$projectId/issues/$issueId"
                          params={{ orgId, projectId, issueId: issue.id }}
                          search={appId ? { app: appId } : {}}
                          translate="no"
                        >
                          {shortId(issue.id, 8, 4)}
                        </Link>{' '}
                        <span translate="no">{issueTitle(issue)}</span>
                      </span>
                      <span className={`kd ${kind}`}>{kind}</span>
                    </li>
                  ) : null,
                )}
              </ul>
            </Section>
          </div>
          <Section
            title={t('health.exits')}
            meta={
              <>
                <span title={t('health.exitsCounted')}>{t('health.exitsPer1k')}</span>
                {hasExits && latestExits
                  ? ` · ${number(allExits(latestExits), {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}${
                      previousExits
                        ? ` ${t('health.vs', {
                            release: number(allExits(previousExits), {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            }),
                          })}`
                        : ''
                    }`
                  : ''}
              </>
            }
            right={
              <Link
                className={btnClass('ghost', 'sm')}
                to="/o/$orgId/p/$projectId/health/exits"
                params={{ orgId, projectId }}
                search={appId ? { app: appId } : {}}
              >
                {t('exits.title')}
              </Link>
            }
          >
            {!hasExits || exitList.length === 0 ? (
              <EmptyNote title={t('exits.none')}>{t('exits.noneBody')}</EmptyNote>
            ) : (
              <Table label={t('health.exitsLabel')}>
                <thead>
                  <tr>
                    <th className="f">{t('exits.col.reason')}</th>
                    <th className="num hl-n" translate="no">
                      {latest.release}
                    </th>
                    <th className="hl-bar">
                      <span className="vh">{t('exits.col.bar')}</span>
                    </th>
                    {previous ? (
                      <>
                        <th className="num hl-n" translate="no">
                          {previous.release}
                        </th>
                        <th className="num hl-n last">{t('exits.col.change')}</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {exitList.map((x) => (
                    <HovRow
                      key={x.reason}
                      label={t('exits.open')}
                      onOpen={() =>
                        navigate({
                          to: '/o/$orgId/p/$projectId/health/exits',
                          params: { orgId, projectId },
                          search: appId ? { app: appId } : {},
                        })
                      }
                    >
                      <td className="f">{t(exitLabel(x.reason, android))}</td>
                      <td className="num hl-n">{one(x.v)}</td>
                      <td className={previous ? 'hl-bar' : 'hl-bar last'}>
                        <Hbar share={exitMax ? x.v / exitMax : 0} {...toneOf(x.reason)} />
                      </td>
                      {previous ? (
                        <>
                          <td className="num hl-n">{one(x.p ?? 0)}</td>
                          <td className="num hl-n last">
                            <Change
                              value={x.v - (x.p ?? 0)}
                              worse={x.reason === 'crash' ? 'c-crash' : 'c-warn'}
                            />
                          </td>
                        </>
                      ) : null}
                    </HovRow>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}
    </main>
  )
}
